/* Turns a viewpoint's loaded graph into the points the 3D surface renders.
 *
 * A reducer rather than a stream so the layer above can hand it one object per change and keep
 * the derivation synchronous and testable, matching how the bubble chart's viewpoint layer is
 * driven. */

import type { ArdoqId, APIFieldType } from '@ardoq/api-types';
import { APIEntityType, APIFieldType as FieldType } from '@ardoq/api-types';
import type { PlottedContext } from './plottedContext';
import type { FormattingAndLabelRules } from '@ardoq/data-model';
import type { PermissionContext } from '@ardoq/access-control';
import { type Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import type { FilterColorData } from '@ardoq/scope-data';
import {
  getLabelsInterfaceForEnhancedScopeData,
  getLabelWithFormattingForViewsNotSupportingMultilabeling,
  type LoadedGraphWithViewpointMode,
} from '@ardoq/graph';
import {
  resolveXyzFields,
  toBinCounts,
  toBinnedHorizontalAxes,
  toPerValueAxes,
  toPerValueBinCounts,
  toXyzDataPoints,
  toXyzValues,
  type AxisCounts,
  type PerValueAxes,
  type RequiredDimension,
  type Surface3DViewSettings,
  type XyzFieldSelection,
  type XyzDataPoint,
  type HorizontalAxes,
  type SurfaceColoring,
  toNiceHorizontalAxes,
} from '@ardoq/xyz-charts';
import { getFieldsForViewpoint } from 'streams/fields/getFieldsForViewpoint';
import { toFieldOptions } from 'tabview/chartShared/fieldLabel';
import { createScopeDataReader } from './readers';
import {
  toFieldColoringLegendData,
  toSurfaceColoring,
  type FieldColoringLegendData,
} from './surfaceColoring';

/**
 * The only part of the settings that can move a point.
 *
 * Narrower than `Surface3DViewSettings` on purpose, and the whole object is still assignable to
 * it. `Surface3DSourceDataLayer` memoises this derivation, and keying that memo on the settings
 * object rebuilt `points` for a change of ramp or a mesh-lines toggle - which
 * `SurfaceChartController.setInputs` reads as a new array and answers with a re-grid, a re-mesh,
 * new isolines and a new ramp. Declaring the reads is what lets the memo key on them, and stops
 * the next appearance setting quietly joining the list.
 */
export type Surface3DGriddingSelection = XyzFieldSelection & {
  interpolation: Surface3DViewSettings['interpolation'];
  binCount: Surface3DViewSettings['binCount'];
};

export type Surface3DSourceDataArgs = {
  context: PlottedContext;
  viewSettings: Surface3DGriddingSelection;
  loadedGraph: LoadedGraphWithViewpointMode;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  formattingAndLabelRules: FormattingAndLabelRules;
  /**
   * The conditional-formatting colours, warmed by the caller.
   *
   * Threaded rather than read off `filterCache` here. That module is a mutable singleton, so
   * reading it inside the builder hid the dependency from this contract and skipped the warming
   * step entirely - the cache would answer with whatever the previously rendered view left in it.
   * The bubble chart's viewpoint layer warms and threads the same value.
   */
  filterColorData: FilterColorData;
};

/** Why the view has nothing to draw, so the empty state can say something specific. */
export type Surface3DEmptyReason =
  | 'no components'
  | 'no numeric fields'
  | 'no fields selected'
  /** Every component was missing a value for at least one of the three chosen fields. */
  | 'no plottable components'
  /** The plotted positions enclose no area: fewer than three of them, or all on one line. */
  | 'no area to interpolate'
  | null;

export type Surface3DSourceData = {
  viewInstanceId: string;
  points: XyzDataPoint[];
  /**
   * The plane the chart is drawn against, decided here rather than in the chart.
   *
   * Rounded outward to the numbers a reader would name, per axis. The rest of the derivation
   * lives in this layer, and the four XYZ views can only be made to agree about their shared
   * plane if one place decides it.
   */
  axes: HorizontalAxes;
  /** Bins along each axis under `binned`: the `binCount` setting resolved against the plotted values. */
  binCounts: AxisCounts;
  /** Which axes get one bin per value for the plotted values, whatever `binCount` is. */
  perValueAxes: PerValueAxes;
  /** Components left out for having no value on a required field, by field. */
  excludedByDimension: Record<RequiredDimension, number>;
  excludedCount: number;
  /** Every numeric field on the plotted component types, for the field pickers. */
  numericFields: { name: string; label: string }[];
  xAxisTitle: string;
  yAxisTitle: string;
  zAxisTitle: string;
  isViewEmpty: boolean;
  emptyReason: Surface3DEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
  /** What colors the surface: the conditional formatting on its components, else the height. */
  coloring: SurfaceColoring;
  legend: FieldColoringLegendData;
};

const NUMERIC_FIELD_TYPES: APIFieldType[] = [FieldType.NUMBER];

/**
 * Empty for a field this viewpoint does not have, the same as for one nobody picked.
 *
 * `fields` is already scoped to the viewpoint, so a name missing from it is a selection carried
 * in from another one - and falling back to the name would print `annualCost` where every other
 * surface in the app prints `Annual cost`.
 */
const toFieldLabel = (
  fields: { name: string; label: string }[],
  fieldName: string | null
): string => {
  if (fieldName === null) return '';
  return fields.find(({ name }) => name === fieldName)?.label ?? '';
};

/**
 * Distinct plotted positions, which is what decides whether a surface is possible at all.
 *
 * Counted here rather than inferred from an empty grid so the empty state can distinguish "no
 * components" from "every component sits on the same two positions" - the second is common on
 * score fields and the fix for it is different.
 */
/** A plotted position in field units, which is all the area test needs. */
type Position = { x: number; y: number };

const toDistinctPositions = (points: XyzDataPoint[]): Position[] => [
  ...new Map(
    points.map(({ x, y }) => [`${x}|${y}`, { x, y }] as const)
  ).values(),
];

/**
 * How far off a line a position may sit and still count as on it, as a sine of the angle.
 *
 * Read in the grid's own coordinates, where both axes run 0 to 1, so it is an angle rather than a
 * ratio between two fields' units.
 */
const COLLINEAR_SINE_TOLERANCE = 1e-9;

type AxisSpan = { min: number; span: number };

const toAxisSpan = (values: number[]): AxisSpan => {
  const min = values.reduce((lowest, value) => Math.min(lowest, value));
  const max = values.reduce((highest, value) => Math.max(highest, value));
  return { min, span: max - min };
};

/**
 * Whether every position lies on one line, which is the second way to enclose no area.
 *
 * Counting distinct positions is not enough, and the gap is reachable in one click: pick an x
 * field whose plotted components all carry the same value - a version, a flag-like score, a
 * traversal already narrowed to one band - or the same field for x and y. Five components then
 * give five *distinct* positions and no area at all. `toScalarGrid` returns its empty grid,
 * `Surface3DChart` hides both canvases, and what is left is the container's background: a blank
 * panel with no axes and no explanation, under an exclusion notice reading "183 of 183 plotted".
 *
 * `Delaunay` is no help here - it jitters collinear input and triangulates the perturbation - so
 * the answer has to be computed rather than read off an empty triangulation.
 *
 * Measured on each axis's own span, because that is the space the grid it predicts is built in:
 * `toGridCoordinate` maps each axis onto `0…resolution - 1` independently, so the interpolation
 * only ever sees a 1:1 aspect. In field units the same test asks whether one axis is wider than
 * the other by more than `1 / COLLINEAR_SINE_TOLERANCE`, which a cost in a minor currency unit
 * against a 1-to-5 score clears on its own. Nothing is lost by moving: collinearity is exact
 * under a per-axis linear scaling, so only the false positives go.
 */
export const isCollinear = (positions: Position[]): boolean => {
  const x = toAxisSpan(positions.map(position => position.x));
  const y = toAxisSpan(positions.map(position => position.y));
  /* One grid column or one grid row. `padDegenerate` gives that a drawable extent, but with three
     distinct positions in it they are still a line. */
  if (x.span === 0 || y.span === 0) return true;

  const scaled = positions.map(position => ({
    x: (position.x - x.min) / x.span,
    y: (position.y - y.min) / y.span,
  }));
  const [origin] = scaled;
  const offsets = scaled
    .map(position => ({ dx: position.x - origin.x, dy: position.y - origin.y }))
    .filter(({ dx, dy }) => dx !== 0 || dy !== 0);

  const direction = offsets[0];
  if (!direction) return true;
  const scale = Math.hypot(direction.dx, direction.dy);

  return offsets.every(
    ({ dx, dy }) =>
      Math.abs(direction.dx * dy - direction.dy * dx) <=
      COLLINEAR_SINE_TOLERANCE * scale * Math.hypot(dx, dy)
  );
};

type Binning = Pick<Surface3DSourceData, 'axes' | 'binCounts' | 'perValueAxes'>;

/**
 * The axes and bin counts for the plotted points.
 *
 * Under `binned` each axis end is padded just enough that its edge column is drawn full width.
 */
export const toSurfaceBinning = (
  points: XyzDataPoint[],
  {
    interpolation,
    binCount,
  }: Pick<Surface3DGriddingSelection, 'interpolation' | 'binCount'>
): Binning => {
  const perValue = toPerValueBinCounts(points);
  const binCounts = toBinCounts(binCount, perValue);
  const axes = toNiceHorizontalAxes(points);

  return {
    axes:
      interpolation === 'binned'
        ? toBinnedHorizontalAxes(axes, points, binCounts)
        : axes,
    binCounts,
    perValueAxes: toPerValueAxes(perValue),
  };
};

export const buildSurfaceSourceData = (
  viewInstanceId: string,
  {
    context,
    viewSettings,
    loadedGraph,
    permissionContext,
    locale,
    i18nLocale,
    formattingAndLabelRules,
    filterColorData,
  }: Surface3DSourceDataArgs
): Surface3DSourceData => {
  const enhancedScopeData = loadedGraph.scopeData;
  const componentIds = loadedGraph.scopeComponentIds;

  const relevantComponentTypeIds = new Set<ArdoqId>(
    componentIds
      .map(
        componentId =>
          enhancedScopeData.componentsById[componentId]?.typeId ?? null
      )
      .filter((typeId): typeId is string => typeId !== null)
  );

  const numericFields = toFieldOptions(
    getFieldsForViewpoint({
      scopeData: enhancedScopeData,
      permissionContext,
      workspaceIds: context.workspaceIds,
      types: NUMERIC_FIELD_TYPES,
      relevantComponentTypeIds,
    })
  );

  const base = {
    viewInstanceId,
    numericFields,
    xAxisTitle: toFieldLabel(numericFields, viewSettings.selectedFieldNameX),
    yAxisTitle: toFieldLabel(numericFields, viewSettings.selectedFieldNameY),
    zAxisTitle: toFieldLabel(numericFields, viewSettings.selectedFieldNameZ),
    locale,
    i18nLocale,
    legend: toFieldColoringLegendData(
      enhancedScopeData,
      formattingAndLabelRules.rules,
      i18nLocale
    ),
  };

  const empty = (emptyReason: Surface3DEmptyReason): Surface3DSourceData => ({
    ...base,
    coloring: { kind: 'height' },
    points: [],
    ...toSurfaceBinning([], viewSettings),
    excludedByDimension: { x: 0, y: 0, z: 0 },
    excludedCount: 0,
    isViewEmpty: true,
    emptyReason,
  });

  if (componentIds.length === 0) return empty('no components');
  if (numericFields.length === 0) return empty('no numeric fields');

  const fields = resolveXyzFields(
    viewSettings,
    new Set(numericFields.map(({ name }) => name))
  );
  if (!fields) return empty('no fields selected');

  const labelsInterface = getLabelsInterfaceForEnhancedScopeData({
    enhancedScopeData,
    formattingFilters: formattingAndLabelRules.rules,
    i18nLocale,
  });

  const labels = new Map<ArdoqId, string>(
    componentIds.map(componentId => [
      componentId,
      getLabelWithFormattingForViewsNotSupportingMultilabeling({
        modelId: componentId,
        entityLabel: enhancedScopeData.componentsById[componentId]?.name ?? '',
        entityType: APIEntityType.COMPONENT,
        i18nLocale,
        referenceLabelFilters: [],
        labelsInterface,
      }),
    ])
  );

  const values = toXyzValues({
    reader: createScopeDataReader({
      enhancedScopeData,
      filterColorData,
      labels,
    }),
    componentIds,
    fields,
    highlightedComponentId: context.componentId || null,
  });

  const { points, excludedByDimension, excludedCount } = toXyzDataPoints({
    values,
  });

  /* Nothing could be placed at all, which is not the same as everything landing close together
     and does not have the same remedy. `getFieldsForViewpoint` offers every numeric field on any
     plotted component type, so on a mixed traversal a user can pick three fields that no single
     component carries and arrive here directly - and telling them to spread their components
     across the plane is advice they cannot act on. The counts say what actually happened, so
     they are carried out rather than zeroed. */
  if (points.length === 0) {
    return {
      ...empty('no plottable components'),
      excludedByDimension,
      excludedCount,
    };
  }

  /* Three positions is the floor for a triangulation with any area, and three on a line still
     enclose none. Reported as its own empty reason because it is the case score fields hit, and
     the remedy - pick fields that spread the components - is not the remedy for having no fields
     selected.

     A requirement of the triangulation, so a requirement of `linear` alone. `fillSmooth` writes
     every cell it visits - Shepard weights are strictly positive - and `fillBinned` rounds each
     sample into a cell that `padDegenerate` keeps in range, so neither can hand back an empty
     grid while there is a point to place. Asking the other two for an area they never needed
     answered "Choose fields that spread your components across the plane" to someone who had
     just chosen "Only where components are". */
  const positions = toDistinctPositions(points);
  if (
    viewSettings.interpolation === 'linear' &&
    (positions.length < 3 || isCollinear(positions))
  ) {
    return {
      ...empty('no area to interpolate'),
      points,
      /* Off the plotted points, which is the population the chart draws. */
      ...toSurfaceBinning(points, viewSettings),
      excludedByDimension,
      excludedCount,
    };
  }

  return {
    ...base,
    points,
    coloring: toSurfaceColoring({
      points,
      rules: formattingAndLabelRules.rules,
      filterColorData,
      enhancedScopeData,
    }),
    /* Off the plotted points, which is the population the chart draws. */
    ...toSurfaceBinning(points, viewSettings),
    excludedByDimension,
    excludedCount,
    isViewEmpty: false,
    emptyReason: null,
  };
};
